/**
 * The brief-order pipeline, declared once and shared by three consumers:
 * the API route that seeds `brief_run_steps`, the durable workflow that
 * executes them, and the dialog that renders them.
 *
 * The rule this file exists to enforce: **a step may not report a generic
 * null.** Every terminal state names the thing the step looked for and the
 * thing it did or did not find. `finding` is required by a CHECK constraint
 * on `brief_run_steps` (see supabase/migrations/85_brief_runs_and_steps.sql),
 * and the `*Finding` builders below are the only sanctioned way to produce
 * one — they take the real counts as arguments so they cannot describe a
 * search that never happened.
 *
 * "No data" / "Nothing found" / "Skipped" are failures here, not fallbacks.
 */

import type { BriefOutcome } from '../paid-media/measurement-plan';

import type { DeclaredChannel } from './declared-brief-intent';

/**
 * ORDER IS A DEPENDENCY GRAPH, not a presentation choice.
 *
 * The brand kit grounds the audiences (an audience is partly derived from
 * `user_brand_voices.audience_description`); the audiences and the competitors
 * both gate which LANES the angle step is even allowed to use; the winners have
 * to be pulled before they can be decoded, and a decoded pattern is an input to
 * the angles. Composition is last because it consumes all of it.
 *
 * `map_competitors` runs BEFORE `research_angles` — the spec that produced this
 * file had it after, which would have shown the user a competitor read they
 * had already been shown the consequences of.
 */
export const BRIEF_PIPELINE_STEPS = [
  { key: 'anchor_source', label: 'Read the source' },
  { key: 'classify_offer', label: 'Classify the offer' },
  { key: 'load_brand', label: 'Load the brand kit' },
  { key: 'derive_audiences', label: 'Derive the audiences' },
  { key: 'pull_winners', label: 'Pull account winners' },
  { key: 'decode_winners', label: 'Decode what worked' },
  { key: 'map_competitors', label: 'Map competitors' },
  { key: 'research_angles', label: 'Research angles' },
  { key: 'pick_references', label: 'Pick visual references' },
  { key: 'compose_pieces', label: 'Compose the pieces' },
  // Lane F4 (founder overrule 2026-09-24) — writes real, grounded paid ad
  // copy for every paid Concept `compose_pieces` just persisted, so the
  // Brief card's own live-polling `AdUnitPreview` has a real `ad_units` row
  // to render rather than a compact "Write ad copy" row for every Concept.
  // Refusals/failures per-Concept never fail this step or the run — see
  // `writePaidCopyFinding`'s doc comment.
  { key: 'write_paid_copy', label: 'Write the ad copy' },
] as const;

export type BriefStepKey = (typeof BRIEF_PIPELINE_STEPS)[number]['key'];

export type BriefRunStepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

/**
 * `found` — the step got what it went looking for.
 * `partial` — it got some of it and the sentence says which part is missing.
 * `none` — it found nothing, and the sentence says what was searched and why
 *   the omission is safe. There is deliberately no fourth value: a step that
 *   cannot classify its own result has not finished thinking.
 */
export type BriefFindingKind = 'found' | 'partial' | 'none';

export interface BriefFinding {
  finding: string;
  findingKind: BriefFindingKind;
  detail?: Record<string, unknown>;
}

/**
 * Both `start-brief-order.ts` (chat) and `NewBriefDialog.tsx` still send
 * `sourceKind: 'product_url'` for ANY pasted URL, sight unseen — classifying
 * a URL's TITLE (product vs. web page) requires a resolved page, which
 * neither client has until after it posts. `server/api/briefs/order.post.ts`
 * — the ONE route both of them call — reclassifies it to `web_url` there,
 * before the run is created, using a real signal (catalog title-token
 * overlap: `classify-url-source.ts`). `anchor_source` reclassifies again as
 * a belt-and-suspenders no-op for any run created before that fix — see
 * `materialize-source-evidence.ts`. Both values share the same
 * `source_url`-not-null shape (`brief_runs_source_shape`,
 * `supabase/migrations/20260923180000_web_source_brief_evidence.sql`).
 */
export type BriefSourceKind = 'product_url' | 'catalog_product' | 'topic' | 'web_url';

export interface BriefRunSourceInput {
  kind: BriefSourceKind;
  /** Required when kind === 'product_url' or 'web_url'. */
  url?: string;
  /** Required when kind === 'catalog_product' — a `shopify_products.id`. */
  productId?: string;
  /** Required when kind === 'topic' — a services business has no product page. */
  topic?: string;
}

export interface BriefRunInput {
  briefId: string;
  runId: string;
  ownerId: string;
  source: BriefRunSourceInput;
  direction: string | null;
  conceptCount: number;
  /** `brief_runs.declared_channel` — the ad channel the person chose at ordering, or null. */
  declaredChannel?: DeclaredChannel | null;
  /** `brief_runs.declared_objective` — the outcome the person chose at ordering, or null. */
  declaredObjective?: BriefOutcome | null;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

const MAX_BRIEF_NAME_CHARS = 80;

/**
 * A brief name is a label in a list, not a description.
 *
 * Cut at the first sentence boundary rather than mid-word at a character
 * count. A topic-sourced brief was landing in the list as "Year-round tax
 * planning for Texas small business owners. We handle S-corp elections,
 * quarterly estimates and bookkeeping" — a paragraph pretending to be a title.
 * The full text is preserved on `brief_runs.source_topic`; only the label is
 * shortened.
 */
export function briefDisplayName(text: string): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Untitled brief';

  const firstSentence = cleaned.split(/(?<=[.!?])\s/)[0] ?? cleaned;
  const candidate = firstSentence.length <= MAX_BRIEF_NAME_CHARS ? firstSentence : cleaned;
  if (candidate.length <= MAX_BRIEF_NAME_CHARS) return candidate.replace(/[.!?]+$/, '');

  const cut = candidate.slice(0, MAX_BRIEF_NAME_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = lastSpace > MAX_BRIEF_NAME_CHARS * 0.5 ? cut.slice(0, lastSpace) : cut;
  return `${trimmed.trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Finding builders. One per step. Each takes the measured facts, so the
// sentence can never claim more than the step actually established.
// ---------------------------------------------------------------------------

export function anchorSourceFinding(input: {
  kind: BriefSourceKind;
  title: string;
  claimCount: number;
  catalogMatches: number | null;
  /** Titles of further pages named in the direction line that WERE read. */
  additionalTitles?: string[];
  /** Pages named in the direction line that could not be read, with why. */
  unreadSources?: { url: string; reason: string }[];
  /** Which pages' result figures read as the brand's own (`resultOwnershipNote`). */
  resultOwnership?: string;
}): BriefFinding {
  const { kind, title, claimCount, catalogMatches } = input;
  const additionalTitles = input.additionalTitles ?? [];
  const unreadSources = input.unreadSources ?? [];
  const alsoRead =
    additionalTitles.length > 0
      ? ` Also read ${plural(additionalTitles.length, 'more page')} you named: ${additionalTitles.map((t) => `"${t}"`).join(', ')}.`
      : '';
  const couldNotRead =
    unreadSources.length > 0
      ? ` Could not read ${unreadSources.map((u) => `${u.url} (${u.reason})`).join('; ')}.`
      : '';
  const ownership = input.resultOwnership ? ` ${input.resultOwnership}` : '';
  const extras = `${alsoRead}${couldNotRead}${ownership}`;
  const what =
    kind === 'product_url'
      ? 'Read the product page'
      : kind === 'web_url'
        ? 'Read the source page'
        : kind === 'catalog_product'
          ? 'Read the catalog product'
          : 'Read the offer you described';

  const parts = [`${what} — "${title}"`, plural(claimCount, 'claim')];
  if (catalogMatches !== null)
    parts.push(plural(catalogMatches, 'catalog match', 'catalog matches'));

  if (claimCount === 0) {
    return {
      finding: `${what} — "${title}" — but the page states no product claims we could quote, so the angles below are inferred from the title and description alone.${extras}`,
      findingKind: 'partial',
      detail: { title, claimCount, catalogMatches },
    };
  }

  return {
    finding: extras ? `${parts.join(', ')}.${extras}` : parts.join(', '),
    findingKind: unreadSources.length > 0 ? 'partial' : 'found',
    detail: {
      title,
      claimCount,
      catalogMatches,
      additionalTitles,
      unreadSources,
      resultOwnership: input.resultOwnership ?? null,
    },
  };
}

export function researchAnglesFinding(input: {
  researchedCount: number;
  hypothesisCount: number;
  /** Angles the model proposed that failed a gate. Reported, never hidden. */
  droppedCount?: number;
  /** Why the biggest group of them was dropped, in the user's language. */
  topDropReason?: string | null;
  /** How many of the twelve lanes this account had the evidence to use. */
  openLaneCount?: number;
  /** Milliseconds the ranker spent: firstCallMs, repairRoundMs, repairSkippedPastDeadline, totalMs. */
  timings?: Record<string, number>;
}): BriefFinding {
  const { researchedCount, hypothesisCount, droppedCount = 0, topDropReason = null } = input;

  const dropClause =
    droppedCount > 0
      ? ` ${plural(droppedCount, 'angle')} dropped${topDropReason ? ` — ${topDropReason}` : ''}.`
      : '';

  if (researchedCount === 0 && hypothesisCount === 0) {
    return {
      finding: droppedCount
        ? `Every angle proposed was dropped before it reached you${topDropReason ? ` — ${topDropReason}` : ''}.`
        : 'No researched angles on file, and the source gave no claims to build a hypothesis from — angles omitted.',
      findingKind: 'none',
      detail: input,
    };
  }

  if (researchedCount === 0) {
    return {
      finding: `${plural(researchedCount, 'researched angle')} — ${plural(
        hypothesisCount,
        'hypothesis angle',
      )} from the source claims (unproven).${dropClause}`,
      findingKind: 'partial',
      detail: input,
    };
  }

  return {
    finding: `${plural(researchedCount, 'evidenced angle')}, plus ${plural(
      hypothesisCount,
      'hypothesis angle',
    )} from the source claims.${dropClause}`,
    findingKind: 'found',
    detail: input,
  };
}

/**
 * The class decides what job every downstream piece of copy has, so the
 * sentence has to name the class AND what decided it. `unclear` is a real
 * answer here, not a failure — it narrows the run rather than guessing.
 */
export function classifyOfferFinding(input: {
  offerClass: 'commerce' | 'service' | 'authority' | 'unclear';
  evidenceCount: number;
  confidence: 'high' | 'medium' | 'low';
  conflictNote: string | null;
}): BriefFinding {
  const { offerClass, evidenceCount, confidence, conflictNote } = input;

  if (offerClass === 'unclear') {
    return {
      finding:
        'Nothing in the source or the account says whether this sells a product, a service or a point of view — the run continues on evidence-only angles.',
      findingKind: 'none',
      detail: input,
    };
  }

  const label =
    offerClass === 'commerce'
      ? 'sells a product — the copy has to end at a checkout'
      : offerClass === 'service'
        ? 'sells a service — the copy has to end in a conversation'
        : 'builds authority — the copy has to earn a follow, not a sale';

  if (conflictNote || confidence !== 'high') {
    return {
      finding: `This ${label}. ${conflictNote ?? `Read off ${plural(evidenceCount, 'source line')}, with ${confidence} confidence.`}`,
      findingKind: 'partial',
      detail: input,
    };
  }

  return {
    finding: `This ${label}. ${plural(evidenceCount, 'source line')} says so.`,
    findingKind: 'found',
    detail: input,
  };
}

const CLAIM_REFUSAL =
  /appears in the copy but in none of the evidence|puts words in someone's mouth/;

/**
 * Names the real cause of a refusal. "Claiming something nothing on file
 * supports" is true only of a number or quote with no source; a piece refused
 * for being generic, for a stacked negation or for a missing proof is not that,
 * and saying so told a reader the wrong thing about a Concept they could not see.
 */
function refusalSentence(
  refused: number,
  refusals: ReadonlyArray<{ hook: string; reason: string }>,
): string {
  const lead = `${plural(refused, 'more was', 'more were')} written and refused`;
  if (refusals.length === 0) return `${lead}; the reason was not recorded.`;
  if (refusals.every((r) => CLAIM_REFUSAL.test(r.reason))) {
    return `${lead} for claiming something nothing on file supports (${refusals[0]!.reason.replace(/\.$/, '')}).`;
  }
  const reasons = [...new Set(refusals.map((r) => r.reason.replace(/\.$/, '')))].slice(0, 3);
  return `${lead}: ${reasons.join('; ')}.`;
}

export function deriveAudiencesFinding(input: {
  droppedCount: number;
  derivedNames: string[];
  /** Earlier audiences that were KEPT for this brief (they share a subject with the source). */
  existingNames: string[];
  /** Earlier audiences on file that were NOT attached to this brief, and share a subject with it. */
  leftOutNames?: string[];
  /** Earlier audiences on file that share nothing with this source: counted, never named. */
  unrelatedCount?: number;
  /** Derived audiences that were already on file under the same name and were reused. */
  reusedNames?: string[];
}): BriefFinding {
  const { droppedCount, derivedNames, existingNames } = input;
  const leftOutNames = input.leftOutNames ?? [];
  const unrelatedCount = input.unrelatedCount ?? 0;
  const unrelatedPhrase = unrelatedCount > 0 ? plural(unrelatedCount, 'unrelated audience') : '';
  const leftOut =
    leftOutNames.length > 0
      ? ` ${plural(leftOutNames.length, 'other audience')} on file ${leftOutNames.length === 1 ? 'was' : 'were'} left out — not cited to this source (${leftOutNames.join(', ')})${unrelatedPhrase ? `, and ${unrelatedPhrase} on file` : ''}.`
      : unrelatedPhrase
        ? ` ${unrelatedPhrase} on file ${unrelatedCount === 1 ? 'was' : 'were'} left out.`
        : '';
  const keptCount = derivedNames.length;
  const existingCount = existingNames.length;

  // An audience already on file was cited to whatever source created it —
  // never to THIS one. Saying "cited to the source" about it is the exact
  // laundering this pipeline exists to refuse, so carried-over audiences are
  // always named as carried over.
  const carriedOver =
    existingCount > 0
      ? `${plural(existingCount, 'audience')} already on file (${existingNames.join(', ')}) — carried over from earlier work, not cited to this source.`
      : '';

  if (keptCount === 0 && existingCount === 0) {
    return {
      finding: droppedCount
        ? `${plural(droppedCount, 'audience')} proposed and every one dropped — none of them could be traced to a line in the source. The set is written to one implicit reader.${leftOut}`
        : `The source does not describe who this is for, so the set is written to one implicit reader rather than an invented persona.${leftOut}`,
      findingKind: 'none',
      detail: input,
    };
  }

  if (keptCount === 0) {
    return {
      finding: `Nothing in this source points at a new audience. ${carriedOver}${leftOut}`,
      findingKind: 'partial',
      detail: input,
    };
  }

  const reusedCount = input.reusedNames?.length ?? 0;
  const derived = `${plural(keptCount, 'audience')} derived and cited to this source (${derivedNames.join(', ')})${reusedCount > 0 ? `; ${reusedCount === keptCount ? 'all' : reusedCount} already on file under the same name, so the existing ${reusedCount === 1 ? 'row was' : 'rows were'} reused` : ''}`;
  const dropped = droppedCount
    ? `; ${droppedCount} dropped for having no line in the source to point at`
    : '';

  return {
    finding: `${derived}${dropped}.${carriedOver ? ` ${carriedOver}` : ''}${leftOut}`,
    findingKind: droppedCount > 0 ? 'partial' : 'found',
    detail: input,
  };
}

/**
 * The gate is the finding. Below five posts with performance data the model is
 * not called at all, and the sentence says the count rather than implying a
 * pattern was looked for and not found.
 */
export function decodeWinnersFinding(input: {
  postsWithMetrics: number;
  minimum: number;
  patternCount: number;
  topPattern: string | null;
}): BriefFinding {
  const { postsWithMetrics, minimum, patternCount, topPattern } = input;

  if (postsWithMetrics < minimum) {
    return {
      finding: `${plural(postsWithMetrics, 'post')} with performance data — under ${minimum}, which is not enough to call anything a pattern, so nothing was claimed.`,
      findingKind: 'none',
      detail: input,
    };
  }

  if (patternCount === 0) {
    return {
      finding: `Read ${plural(postsWithMetrics, 'post')} with performance data and found nothing they share — no pattern claimed.`,
      findingKind: 'none',
      detail: input,
    };
  }

  return {
    finding: `${plural(patternCount, 'pattern')} across ${plural(postsWithMetrics, 'post')}${topPattern ? `: ${topPattern}` : ''}.`,
    findingKind: 'found',
    detail: input,
  };
}

export function loadBrandFinding(input: {
  kitName: string | null;
  toneSet: boolean;
  pillarCount: number;
}): BriefFinding {
  const { kitName, toneSet, pillarCount } = input;

  if (!kitName) {
    return {
      finding:
        'No brand kit on this account — copy will be written without your name, colours, tone or pillars.',
      findingKind: 'none',
      detail: input,
    };
  }

  const gaps: string[] = [];
  if (!toneSet) gaps.push('no tone set');
  if (pillarCount === 0) gaps.push('no content pillars');

  if (gaps.length > 0) {
    return {
      finding: `Brand kit: ${kitName} — ${gaps.join(', ')}, so voice comes from the source page rather than from you.`,
      findingKind: 'partial',
      detail: input,
    };
  }

  return {
    finding: `Brand kit: ${kitName} — tone set, ${plural(pillarCount, 'content pillar')}.`,
    findingKind: 'found',
    detail: input,
  };
}

export function pullWinnersFinding(input: {
  publishedCount: number;
  withMetricsCount: number;
  topLabel: string | null;
}): BriefFinding {
  const { publishedCount, withMetricsCount, topLabel } = input;

  if (publishedCount === 0) {
    return {
      finding:
        'This account has published nothing yet, so there are no winners to learn from — the brief leans entirely on the source page.',
      findingKind: 'none',
      detail: input,
    };
  }

  if (withMetricsCount === 0) {
    return {
      finding: `${plural(publishedCount, 'published post')} on file but none has performance data yet — winners omitted.`,
      findingKind: 'none',
      detail: input,
    };
  }

  return {
    finding: `${plural(withMetricsCount, 'published post')} with performance data — best performer: ${topLabel}.`,
    findingKind: 'found',
    detail: input,
  };
}

export function mapCompetitorsFinding(input: { names: string[] }): BriefFinding {
  const { names } = input;

  if (names.length === 0) {
    return {
      // Names the real sources and the place to fix it. The previous wording,
      // "Your brand voice names no competitors and none were detected from
      // your site", described only the website-scan blob — so when grounding
      // began reading the `competitors` table too (2026-09-09) it was both
      // incomplete and, for anyone who had added competitors in Settings,
      // simply untrue.
      finding:
        'No competitors are tracked for this workspace — competitor context omitted. Add them in Settings → Competitors.',
      findingKind: 'none',
      detail: input,
    };
  }

  return {
    finding: `${plural(names.length, 'named competitor')}: ${names.slice(0, 3).join(', ')}${
      names.length > 3 ? '…' : ''
    }.`,
    findingKind: 'found',
    detail: input,
  };
}

export function pickReferencesFinding(input: {
  lookName: string | null;
  productImageCount: number;
  libraryImageCount: number;
}): BriefFinding {
  const { lookName, productImageCount, libraryImageCount } = input;
  const total = productImageCount + libraryImageCount;

  if (!lookName && total === 0) {
    return {
      finding:
        'No saved Look, no product photography and no image in your library — every concept ships without a visual reference.',
      findingKind: 'none',
      detail: input,
    };
  }

  if (!lookName) {
    return {
      finding: `${plural(total, 'image')} available to reference, but no saved Look — the concepts will not share a visual style.`,
      findingKind: 'partial',
      detail: input,
    };
  }

  if (total === 0) {
    return {
      finding: `Look: ${lookName} — but no product photo or library image to anchor it to.`,
      findingKind: 'partial',
      detail: input,
    };
  }

  return {
    finding: `Look: ${lookName}, ${plural(total, 'reference image')}.`,
    findingKind: 'found',
    detail: input,
  };
}

/**
 * Reports the concepts by what they actually CONTAIN, not by how many rows
 * were inserted.
 *
 * The first version said "N concepts written, each with a hook, a body and a
 * CTA" whenever N rows landed. Run against the real route it printed exactly
 * that for three concepts whose body and CTA were empty strings — the
 * placeholder composer only carries a hook. A tick beside a false sentence is
 * the failure this whole pipeline exists to prevent, so the body and CTA
 * counts are arguments now and the sentence cannot outrun them.
 */
export function composePiecesFinding(input: {
  requested: number;
  written: number;
  withBody: number;
  withCta: number;
  /** How many carry an angle from the bank. Counted off persisted rows. */
  withAngle?: number;
  /** How many carry a visual direction. Counted off persisted rows. */
  withVisualDirection?: number;
  /** Pieces the anatomy checker refused before anything was written. */
  refused?: number;
  /** Of `refused`, paid pieces dropped because their hook never reached the offer. */
  unbridged?: number;
  /**
   * Each refused piece's hook and the sentence that refused it. A count alone
   * made a refusal impossible to judge afterwards (ITC run 0b33f38d: two
   * refused, no reason kept), and the finding then had to guess a cause.
   */
  refusals?: Array<{ hook: string; reason: string }>;
  /** True when the hook→offer check had nothing to check against (no brand vocabulary or offering). */
  bridgeUnchecked?: boolean;
  /** Concepts that left the step with no card headline (a stated refusal on the Concept). */
  withoutCardHeadline?: number;
  /** Where the step's time went, in ms (`ComposeResult.timings`). Kept in `detail` only. */
  timings?: Record<string, number>;
  /** Why the depth was cut, when it was. */
  shortfallReason?: string | null;
  /** Why pieces were lost for a cause that is not a refusal (too few returned, repair skipped or discarded). */
  shortfallNotes?: string[];
  /** Pieces whose planned format was changed because the paid platform cannot run it. */
  formatConversions?: Array<{
    hook: string;
    platform: string;
    from: string;
    to: string;
    reason: string;
  }>;
  /**
   * Which platform(s) this run targeted and why — from
   * `resolvePaidPlatformCandidates`/`rankPaidPlatforms`
   * (`src/lib/briefs/paid-channel-rule.ts`). `source: 'ad_accounts'` is the
   * quiet case (a connected ad account decided it, nothing to call out); the
   * other two sources are stated in the sentence below because they mean the
   * platform was a fallback or a default, not a chosen channel.
   */
  channel?: {
    platforms: string[];
    source: 'ad_accounts' | 'organic_fallback' | 'default';
    reason: string;
  } | null;
}): BriefFinding {
  const {
    requested,
    written,
    withBody,
    withCta,
    withAngle,
    withVisualDirection,
    refused = 0,
    unbridged = 0,
    shortfallReason = null,
    shortfallNotes = [],
    formatConversions = [],
    channel = null,
  } = input;

  const formatNote =
    formatConversions.length > 0
      ? ` ${plural(formatConversions.length, 'Concept')} planned as ${[
          ...new Set(formatConversions.map((c) => `a ${c.from}`)),
        ].join(' or ')} will run as a single image (${
          formatConversions.every((c) => c.platform === 'linkedin')
            ? 'LinkedIn paid execution compiles a single image only'
            : 'a paid Concept carries an image'
        }).`
      : '';

  const channelNote =
    channel && channel.source !== 'ad_accounts'
      ? ` Targeting ${channel.platforms.join(', ')} (${
          channel.source === 'organic_fallback'
            ? 'no ad account connected — organic platforms used'
            : 'nothing connected — default platforms used'
        }).`
      : '';

  const bridgeNote = input.bridgeUnchecked
    ? ' The link from each hook to what the account sells could not be checked: no brand vocabulary or offering is on file.'
    : '';

  if (written === 0) {
    return {
      finding:
        (shortfallReason
          ? `Nothing was written — ${shortfallReason}`
          : refused > 0
            ? `${plural(refused, 'concept')} written and every one refused before it reached you.`
            : `Asked for ${plural(requested, 'concept')} and wrote none — the brief is empty.`) +
        formatNote +
        channelNote +
        bridgeNote,
      findingKind: 'none',
      detail: input,
    };
  }

  if (written < requested) {
    // A refusal is not a gap. When the anatomy checker threw a piece out for
    // making a claim it could not source, "the rest are missing" reads as a
    // failure of the pipeline when it is the pipeline doing its job.
    const causes = [
      ...(refused > 0
        ? [
            unbridged >= refused
              ? `${plural(refused, 'more was', 'more were')} written and dropped because the hook never reached what the account sells.`
              : refusalSentence(refused, input.refusals ?? []),
          ]
        : []),
      ...shortfallNotes.map((note) => `${note.charAt(0).toUpperCase()}${note.slice(1)}.`),
    ];
    const why = [shortfallReason, ...causes].filter(Boolean).join(' ') || 'the rest are missing.';
    return {
      finding: `${plural(written, 'concept')} written of ${requested} requested — ${why}${formatNote}${channelNote}${bridgeNote}`,
      findingKind: 'partial',
      detail: input,
    };
  }

  const gaps: string[] = [];
  if (withBody < written) gaps.push(`${written - withBody} without body copy`);
  if (withCta < written) gaps.push(`${written - withCta} without a CTA`);
  if (withAngle !== undefined && withAngle < written) {
    gaps.push(`${written - withAngle} not tied to an angle`);
  }
  if (withVisualDirection !== undefined && withVisualDirection < written) {
    gaps.push(`${written - withVisualDirection} without a visual direction`);
  }
  if (input.withoutCardHeadline && input.withoutCardHeadline > 0) {
    gaps.push(
      `${input.withoutCardHeadline} without a card headline (the evidence it cites could not be turned into one, so it cannot be rendered yet)`,
    );
  }

  if (gaps.length > 0) {
    // "hook only" is kept verbatim for the case that motivated this builder:
    // three rows landed, a tick went green, and every body and CTA was an
    // empty string.
    const prefix = withBody === 0 && withCta === 0 ? 'hook only, ' : '';
    return {
      finding: `${plural(written, 'concept')} written — ${prefix}${gaps.join(' and ')}.${formatNote}${channelNote}${bridgeNote}`,
      findingKind: 'partial',
      detail: input,
    };
  }

  const extras: string[] = [];
  if (withAngle !== undefined) extras.push('an angle');
  if (withVisualDirection !== undefined) extras.push('a visual direction');

  return {
    finding:
      (extras.length === 0
        ? `${plural(written, 'concept')} written, each with a hook, a body and a CTA.`
        : `${plural(written, 'concept')} written, each with a hook, a body, a CTA and ${extras.join(' and ')}.`) +
      formatNote +
      channelNote +
      bridgeNote,
    findingKind: 'found',
    detail: input,
  };
}

/**
 * A refusal or a failure here is a real, honest outcome — never a step
 * failure. `write_paid_copy` always calls `markStepSucceeded` with this
 * finding, whatever the per-Concept mix, because "0 of 2 written, both
 * refused for lacking a citable claim" is exactly the kind of true, specific
 * sentence this whole pipeline exists to produce — the same posture
 * `composePiecesFinding` above already takes toward a refused piece.
 */
export function writePaidCopyFinding(input: {
  totalPaidConcepts: number;
  written: number;
  refused: number;
  failed: number;
  unsupported: number;
}): BriefFinding {
  const { totalPaidConcepts, written, refused, failed, unsupported } = input;

  if (totalPaidConcepts === 0) {
    return {
      finding: 'This run wrote no paid concepts, so there was no ad copy to write.',
      findingKind: 'none',
      detail: input,
    };
  }

  const reasons: string[] = [];
  if (refused > 0) {
    reasons.push(`${plural(refused, 'concept')} refused for lacking a citable claim`);
  }
  if (failed > 0) reasons.push(`${plural(failed, 'concept')} failed`);
  if (unsupported > 0) {
    reasons.push(`${plural(unsupported, 'concept')} on a platform Mora doesn't write copy for yet`);
  }
  const suffix = reasons.length > 0 ? ` — ${reasons.join('; ')}.` : '.';

  return {
    finding: `Ad copy written for ${written} of ${plural(totalPaidConcepts, 'paid concept')}${suffix}`,
    findingKind: written === 0 ? 'none' : written === totalPaidConcepts ? 'found' : 'partial',
    detail: input,
  };
}
